import { useSeoMeta } from '@unhead/react';
import { useNote } from '@/hooks/useNote';
import { NoteContent } from '@/components/NoteContent';
import { OriginalRecord } from '@/components/OriginalRecord';
import { RecordNav } from '@/components/RecordNav';
import { ArchiveBar } from '@/components/ArchiveBar';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { useMemo, type MouseEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { formatDate, formatRelativeTime, getNoteExcerpt, getNoteHeading, getNoteImage } from '@/lib/noteUtils';
import { deriveNoteTags } from '@/lib/noteTags';
import { FACETS } from '@/lib/taxonomy';
import { ArrowLeft, Archive, ExternalLink } from 'lucide-react';
import { nip19 } from 'nostr-tools';
import { ARCHIVE_PUBKEY } from '@/hooks/useArchiveNotes';

interface NotePageProps {
  id: string;
}

export function NotePage({ id }: NotePageProps) {
  const { data: note, isLoading, isError } = useNote(id);

  // If the reader came from the archive feed, "back to the archive" should
  // behave exactly like the browser's Back button, so the feed is restored at
  // the same spot. A record opened from a shared link has no feed behind it,
  // so those controls fall through to a normal link to the home page.
  const location = useLocation();
  const navigate = useNavigate();
  const cameFromArchive =
    (location.state as { fromArchive?: boolean } | null)?.fromArchive === true;
  const backToArchive = (e: MouseEvent) => {
    if (!cameFromArchive) return;
    // Let modified clicks (new tab/window) behave normally.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    navigate(-1);
  };

  const excerpt = note ? getNoteExcerpt(note, 160) : '';
  const noteId = note ? nip19.noteEncode(note.id) : '';

  // The record's terms, grouped by facet in catalogue order.
  const classification = useMemo(() => {
    if (!note) return [];
    const tags = deriveNoteTags(note);
    return FACETS.map((facet) => ({
      facet,
      tags: tags.filter((t) => t.facet === facet.id),
    })).filter((g) => g.tags.length > 0);
  }, [note]);

  // The record's title, found the same way the cards find it, so a record reads
  // as bold here exactly when it does in the feed. Pager intercepts carry no
  // title on their cards, so none here either.
  const recordTitle = useMemo(() => {
    if (!note) return null;
    if (deriveNoteTags(note).some((t) => t.slug === 'intercept')) return null;
    return getNoteHeading(note).title;
  }, [note]);

  // Shared links and browser tabs use the record's title when it has one.
  const headline = note ? (getNoteHeading(note).title ?? excerpt) : '';
  const seoTitle = note
    ? `${headline.slice(0, 60)}${headline.length > 60 ? '…' : ''} — 9/11receipts Archive`
    : 'Loading record — 9/11receipts Archive';
  const seoDescription = excerpt || 'A record from the 9/11receipts archive.';

  // A record's own preview, for anywhere its link is shared. The picture is the
  // record's first image, when it has one (the site's own seal is used
  // otherwise, via the page-level default).
  const previewImage = note ? getNoteImage(note) : null;

  useSeoMeta({
    title: seoTitle,
    description: seoDescription,
    ogTitle: seoTitle,
    ogDescription: seoDescription,
    ogType: 'article',
    ...(previewImage ? { ogImage: previewImage } : {}),
    twitterCard: previewImage ? 'summary_large_image' : 'summary',
    twitterTitle: seoTitle,
    twitterDescription: seoDescription,
    ...(previewImage ? { twitterImage: previewImage } : {}),
  });

  // Guard: only show notes from the archive pubkey
  if (!isLoading && note && note.pubkey !== ARCHIVE_PUBKEY) {
    return (
      <div className="min-h-screen bg-background">
        <ArchiveBar to="/" label="Back to the archive" onClick={backToArchive} />
        <main className="max-w-3xl mx-auto px-4 py-12 text-center">
          <Archive className="w-12 h-12 mx-auto mb-4 text-muted-foreground/40" />
          <p className="font-typewriter text-sm uppercase tracking-wider text-muted-foreground">This note is not part of this archive.</p>
          <Link to="/" onClick={backToArchive}>
            <Button variant="outline" className="mt-6 gap-2 font-typewriter text-xs uppercase tracking-[0.2em] rounded-sm border-foreground/40 hover:bg-foreground hover:text-background">
              <ArrowLeft className="w-4 h-4" /> Back to Archive
            </Button>
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <ArchiveBar to="/" label="Back to the archive" onClick={backToArchive} />

      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Archive index, Random, Next */}
        <RecordNav current={note} onArchive={backToArchive} className="mb-6" />

        {/* Loading */}
        {isLoading && (
          <div className="border border-foreground/25 rounded-sm p-6 space-y-4 shadow-[2px_2px_0_0_rgba(0,0,0,0.05)]">
            <Skeleton className="h-4 w-40" />
            <div className="space-y-2">
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-4/5" />
              <Skeleton className="h-6 w-3/5" />
            </div>
          </div>
        )}

        {/* Error */}
        {isError && !isLoading && (
          <div className="border border-dashed border-destructive rounded-sm p-12 text-center">
            <Archive className="w-10 h-10 mx-auto mb-3 text-destructive/50" />
            <p className="font-typewriter text-sm uppercase tracking-wider text-muted-foreground">Unable to load this record from the archive.</p>
          </div>
        )}

        {/* Not found */}
        {!isLoading && !isError && !note && (
          <div className="border border-dashed border-foreground/30 rounded-sm p-12 text-center">
            <Archive className="w-10 h-10 mx-auto mb-3 text-muted-foreground/40" />
            <p className="font-typewriter text-sm uppercase tracking-wider text-muted-foreground leading-relaxed">
              This record was not found in the archive. It may not be indexed yet.
            </p>
          </div>
        )}

        {/* The record: a flat, ruled document. A heavy rule opens it, hairlines
            divide its parts, and the catalogue classification sits beneath. */}
        {note && (
          <article className="border-t-[3px] border-foreground">
            <header className="flex items-baseline justify-between gap-4 border-b border-foreground/25 py-3">
              <span className="font-engraved text-xs font-semibold uppercase tracking-[0.35em] text-foreground">
                Record
              </span>
              <time
                dateTime={new Date(note.created_at * 1000).toISOString()}
                className="font-mono text-[11px] tabular-nums text-foreground/60"
              >
                {formatDate(note.created_at)}
              </time>
            </header>

            {/* Body */}
            <div className="prose prose-neutral dark:prose-invert max-w-none py-6 md:py-8">
              <NoteContent event={note} title={recordTitle} />
            </div>

            {/* Classification — each term is grouped under its facet and links
                to the archive filtered by that term. */}
            {classification.length > 0 && (
              <section aria-label="Classification" className="border-t border-foreground/25">
                <h2 className="sr-only">Classification</h2>
                <dl>
                  {classification.map(({ facet, tags }) => (
                    <div
                      key={facet.id}
                      className="grid gap-1.5 border-b border-foreground/15 py-3 sm:grid-cols-[9.5rem_1fr] sm:gap-6"
                    >
                      <dt className="font-engraved text-[10px] font-semibold uppercase tracking-[0.2em] text-foreground/60 sm:pt-1">
                        {facet.label}
                      </dt>
                      <dd className="flex flex-wrap gap-2">
                        {tags.map((tag) => (
                          <Link
                            key={tag.slug}
                            to={`/?t=${encodeURIComponent(tag.slug)}`}
                            className="inline-flex items-center rounded-sm border border-foreground/25 px-2.5 py-1 font-display text-[15px] leading-none text-foreground transition-colors hover:border-foreground hover:bg-foreground hover:text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                          >
                            {tag.label}
                          </Link>
                        ))}
                      </dd>
                    </div>
                  ))}
                </dl>
              </section>
            )}

            {/* Authentication — the record's cryptographic identity. */}
            <footer className="pt-6">
              <h2 className="font-engraved text-[10px] font-semibold uppercase tracking-[0.3em] text-foreground/70">
                Authentication
              </h2>
              <dl className="mt-3 grid gap-3">
                <div>
                  <dt className="font-engraved text-[10px] font-semibold uppercase tracking-[0.2em] text-foreground/70">
                    Record ID
                  </dt>
                  <dd className="mt-1 break-all font-mono text-xs text-foreground/70">{note.id}</dd>
                </div>
                <div>
                  <dt className="font-engraved text-[10px] font-semibold uppercase tracking-[0.2em] text-foreground/70">
                    Signature
                  </dt>
                  <dd className="mt-1 break-all font-mono text-xs text-foreground/70">
                    {note.sig.slice(0, 32)}…{note.sig.slice(-16)}
                  </dd>
                </div>
              </dl>

              <OriginalRecord event={note} noteId={noteId} />

              <div className="mt-5 flex items-center gap-3 border-t border-foreground/15 pt-4">
                <span className="font-mono text-[11px] text-foreground/70">
                  {formatRelativeTime(note.created_at)}
                </span>
                <span className="flex-1" />
                <a
                  href={`https://njump.me/${noteId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 font-engraved text-[10px] font-semibold uppercase tracking-[0.2em] text-foreground/70 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                  View on Nostr
                </a>
              </div>
            </footer>
          </article>
        )}

        {/* Bottom nav: the same row, for a reader who has just finished */}
        <RecordNav current={note} onArchive={backToArchive} className="mt-10" />
      </main>
    </div>
  );
}
